package com.kinoshazam.analytics

import analytics.AnalyticsEvent

object AppEvents {
    fun appOpened() = AnalyticsEvent("app_opened")

    fun searchSubmitted(
        queryLength: Int,
        resultCount: Int,
    ) = AnalyticsEvent(
        name = "search_submitted",
        properties = mapOf(
            "query_length" to queryLength,
            "result_count" to resultCount,
        ),
    )

    fun identifySubmitted(
        queryLength: Int,
        resultCount: Int,
    ) = AnalyticsEvent(
        name = "identify_submitted",
        properties = mapOf(
            "query_length" to queryLength,
            "result_count" to resultCount,
        ),
    )

    fun videoOpened(
        videoId: Int,
        isSerial: Boolean,
    ) = AnalyticsEvent(
        name = "video_opened",
        properties = mapOf(
            "video_id" to videoId,
            "is_serial" to isSerial,
        ),
    )

    fun personOpened(personId: Int) = AnalyticsEvent(
        name = "person_opened",
        properties = mapOf("person_id" to personId),
    )

    fun favoriteToggled(
        videoId: Int,
        isSerial: Boolean,
        added: Boolean,
    ) = AnalyticsEvent(
        name = "favorite_toggled",
        properties = mapOf(
            "video_id" to videoId,
            "is_serial" to isSerial,
            "added" to added,
        ),
    )

    fun playbackStarted(
        videoId: Int,
        isSerial: Boolean,
        source: String,
    ) = AnalyticsEvent(
        name = "playback_started",
        properties = mapOf(
            "video_id" to videoId,
            "is_serial" to isSerial,
            "source" to source,
        ),
    )

    fun loginSucceeded() = AnalyticsEvent("login_succeeded")

    fun loginFailed(reason: String) = AnalyticsEvent(
        name = "login_failed",
        properties = mapOf("reason" to reason),
    )

    fun signUpStarted() = AnalyticsEvent("sign_up_started")

    fun logout() = AnalyticsEvent("logout")

    fun updateShown(versionName: String) = AnalyticsEvent(
        name = "update_shown",
        properties = mapOf("version_name" to versionName),
    )

    fun updateAccepted(versionName: String) = AnalyticsEvent(
        name = "update_accepted",
        properties = mapOf("version_name" to versionName),
    )

    fun tasteSessionSaved(
        duration: String,
        company: String,
        mood: String,
        seedCount: Int,
        readyForPick: Boolean,
    ) = AnalyticsEvent(
        name = "taste_session_saved",
        properties = mapOf(
            "duration" to duration,
            "company" to company,
            "mood" to mood,
            "seed_count" to seedCount,
            "ready_for_pick" to readyForPick,
        ),
    )

    fun tastePickShown(
        videoId: String,
        reasonKey: String,
    ) = AnalyticsEvent(
        name = "taste_pick_shown",
        properties = mapOf(
            "video_id" to videoId,
            "reason_key" to reasonKey,
        ),
    )

    fun tastePartyCreated(
        code: String,
        capacity: Int,
    ) = AnalyticsEvent(
        name = "taste_party_created",
        properties = mapOf(
            "code" to code,
            "capacity" to capacity,
        ),
    )

    fun tastePartyJoined(
        code: String,
        capacity: Int,
    ) = AnalyticsEvent(
        name = "taste_party_joined",
        properties = mapOf(
            "code" to code,
            "capacity" to capacity,
        ),
    )

    fun tasteStepCompleted(
        step: String,
        value: String,
    ) = AnalyticsEvent(
        name = "taste_step_completed",
        properties = mapOf(
            "step" to step,
            "value" to value,
        ),
    )

    fun tasteConstraintsSet(
        excludeTags: List<String>,
        noSubtitles: Boolean,
        excludeWatched: Boolean,
        highRating: Boolean,
        hasSeeds: Boolean,
    ) = AnalyticsEvent(
        name = "taste_constraints_set",
        properties = mapOf(
            "exclude_tags" to excludeTags.sorted().joinToString(","),
            "no_subtitles" to noSubtitles,
            "exclude_watched" to excludeWatched,
            "high_rating" to highRating,
            "has_seeds" to hasSeeds,
        ),
    )

    fun tasteSeedToggled(
        videoId: String,
        added: Boolean,
        seedCount: Int,
    ) = AnalyticsEvent(
        name = "taste_seed_toggled",
        properties = mapOf(
            "video_id" to videoId,
            "added" to added,
            "seed_count" to seedCount,
        ),
    )

    fun tasteSeedsSaved(seedCount: Int) = AnalyticsEvent(
        name = "taste_seeds_saved",
        properties = mapOf("seed_count" to seedCount),
    )

    fun tasteSeedsMoreLoaded(page: Int) = AnalyticsEvent(
        name = "taste_seeds_more_loaded",
        properties = mapOf("page" to page),
    )

    fun tasteSeedsEdited() = AnalyticsEvent("taste_seeds_edited")

    fun tastePickRejected(
        videoId: String,
        kind: String,
        party: Boolean,
    ) = AnalyticsEvent(
        name = "taste_pick_rejected",
        properties = mapOf(
            "video_id" to videoId,
            "kind" to kind,
            "party" to party,
        ),
    )

    fun tastePickWatch(
        videoId: String,
        party: Boolean,
        locked: Boolean,
    ) = AnalyticsEvent(
        name = "taste_pick_watch",
        properties = mapOf(
            "video_id" to videoId,
            "party" to party,
            "locked" to locked,
        ),
    )

    fun tastePickOpened(
        videoId: String,
        isSerial: Boolean,
    ) = AnalyticsEvent(
        name = "taste_pick_opened",
        properties = mapOf(
            "video_id" to videoId,
            "is_serial" to isSerial,
        ),
    )

    fun tastePickFailed(party: Boolean) = AnalyticsEvent(
        name = "taste_pick_failed",
        properties = mapOf("party" to party),
    )

    fun tastePartyVote(
        videoId: String,
        kind: String,
        code: String,
    ) = AnalyticsEvent(
        name = "taste_party_vote",
        properties = mapOf(
            "video_id" to videoId,
            "kind" to kind,
            "code" to code,
        ),
    )

    fun tastePartyLocked(
        videoId: String,
        code: String,
    ) = AnalyticsEvent(
        name = "taste_party_locked",
        properties = mapOf(
            "video_id" to videoId,
            "code" to code,
        ),
    )

    fun tastePartyInviteCopied(code: String) = AnalyticsEvent(
        name = "taste_party_invite_copied",
        properties = mapOf("code" to code),
    )

    fun tastePartyCapacitySet(
        code: String,
        capacity: Int,
    ) = AnalyticsEvent(
        name = "taste_party_capacity_set",
        properties = mapOf(
            "code" to code,
            "capacity" to capacity,
        ),
    )

    fun tastePartyHostPick(code: String) = AnalyticsEvent(
        name = "taste_party_host_pick",
        properties = mapOf("code" to code),
    )

    fun tastePartyJoinFailed(code: String) = AnalyticsEvent(
        name = "taste_party_join_failed",
        properties = mapOf("code" to code),
    )

    fun tasteStartOwnPick() = AnalyticsEvent("taste_start_own_pick")

    fun tasteExited(step: String) = AnalyticsEvent(
        name = "taste_exited",
        properties = mapOf("step" to step),
    )

    fun tasteRetry(step: String) = AnalyticsEvent(
        name = "taste_retry",
        properties = mapOf("step" to step),
    )

    fun tasteError(
        reason: String,
        step: String,
    ) = AnalyticsEvent(
        name = "taste_error",
        properties = mapOf(
            "reason" to reason,
            "step" to step,
        ),
    )
}
